import React, { useEffect, useState } from 'react';
import './App.css';
import { Route, Routes, Outlet, useSearchParams, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

import LoginComponent from './Components/Login/Login';
import ForgottenPasswordComponent from './Components/Login/ForgottenPassword';
import NavigationComponent from './Components/Navigation';
import UnauthorizedComponent from './Components/ErrorPages/Unathorized';
import NotFoundComponent from './Components/ErrorPages/NotFound';
import ListTableComponent from './Components/List/ListTable';
import AuthRoute from './Components/AuthRoute';
import TextTableComponent from './Components/Text/TextTable';
import TextEditComponent from './Components/Text/TextEdit';
import ImageTableComponent from './Components/Image/ImageTable';
import ImageUploadComponent from './Components/Image/ImageUpload';
import GalleryTableComponent from './Components/Gallery/GalleryTable';
import GalleryCreateEdit from './Components/Gallery/GalleryCreateEdit';
import HomeComponent from './Components/Home/Home';
import ToastComponent from './Components/Toast/Toast';
import { Constants } from './Models/Constants';
import { UserService } from './Services/UserService';
import PageImageChangeComponent from "./Components/Image/PageImageChange";
import UserTableComponent from "./Components/User/UserTable";
import UserCreateEditComponent from "./Components/User/UserCreateEdit";
import SetPasswordComponent from "./Components/User/SetPassword";
import ListItemEditComponent from "./Components/ListItem/ListItemEdit";
import ListItemCreateComponent from "./Components/ListItem/ListItemCreate";
import {UserContext, SiteContext, ToastContext, ChangesContext, AdminUsersContext, setToastHandler} from "./Contexts";
import {FrontendEntityChangeService} from "./Services/FrontendEntityChangeService";
import PageTableComponent from "./Components/Page/PageTable";
import SectionTableComponent from "./Components/Section/SectionTable";
import SectionEditComponent from "./Components/Section/SectionEdit";
import PostTableComponent from "./Components/Post/PostTable";
import PostCreateEditComponent from "./Components/Post/PostCreateEdit";
import FileTableComponent from "./Components/File/FileTable";
import FileUploadComponent from "./Components/File/FileUpload";

const PublicLayout = () => <Outlet />;

const MainLayout = () => (
    <div className="main-layout">
        <Outlet/>
    </div>
);

function App() {
    const { user, getAdminUserNames } = UserService();
    const { getChanges } = FrontendEntityChangeService();
    const [currentUser, setCurrentUser] = useState(Constants.defaultUserContext);
    const [currentSite, setCurrentSite] = useState(Constants.defaultSiteContext);
    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adminUserNames, setAdminUserNames] = useState({ adminUserNames: new Map(), loaded: false });

    const userState = { currentUser, setCurrentUser };
    const siteState = { currentSite, setCurrentSite };
    const toastState = { toast, setToast };
    const [hasChanges, setHasChanges] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    useEffect(() => {
        setToastHandler(setToast);
    }, [setToast]);

    // Fetched once for the whole session; tables read from AdminUsersContext instead of
    // resolving "created by / modified by" ids one at a time.
    const fetchAdminUsers = () => {
        getAdminUserNames().then(items => {
            setAdminUserNames({ adminUserNames: new Map(items.map(i => [i.id, i.displayName])), loaded: true });
        });
    };

    useEffect(() => {
        fetchAdminUsers();
    }, []);

    // Keep URL in sync when site changes (replace, not push - this mirrors state into the
    // URL rather than representing a real navigation, so it must not create history entries)
    useEffect(() => {
        if (currentSite.id && currentSite.id !== -1) {
            setSearchParams({ websiteId: currentSite.id.toString() }, { replace: true });
        }
    }, [currentSite, setSearchParams]);

    // Fetch user on any non-public route
    useEffect(() => {
        const publicRoutes = ['/', '/401', '/404', '/forgottenpassword'];
        if (publicRoutes.includes(location.pathname)) {
            setLoading(false);
            return;
        }

        user()
            .then(res => {
                setCurrentUser({
                    id: res.id,
                    displayName: res.displayName,
                    email: res.email,
                    isSuperAdmin: res.isSuperAdmin,
                    isPasswordTemporary: res.isPasswordTemporary,
                    websites: res.websites
                });
            })
            .catch(() => {
                setCurrentUser(Constants.defaultUserContext);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [location.pathname]);

    useEffect(() => {
        const run = async () => {
            localStorage.setItem("tabName", currentSite.shortName);

            if (currentUser.id === Constants.defaultUserContext.id) {
                return;
            }
            const result = await getChanges(currentSite.id);
            setHasChanges(result.totalCount !== 0);
        };

        run();
    }, [currentSite]);

    if (loading) {
        return <></>;
    }

    const auth = (element) => <AuthRoute element={element} />;
    const mainRoutes : [{path: string, element: any}] = [
        { path: "/post/table", element: <PostTableComponent /> },
        { path: "/post/create", element: <PostCreateEditComponent isEditMode={false} /> },
        { path: "/post/edit/:blogId", element: <PostCreateEditComponent isEditMode={true} /> },

        { path: "/list/table", element: <ListTableComponent /> },
        { path: "/listitem/edit/:listItemId", element: <ListItemEditComponent /> },
        { path: "/listitem/create/:listId", element: <ListItemCreateComponent /> },

        { path: "/text/table", element: <TextTableComponent /> },
        { path: "/text/table/:pageId", element: <TextTableComponent /> },
        { path: "/text/edit/:textId", element: <TextEditComponent /> },

        { path: "/image/table", element: <ImageTableComponent /> },
        { path: "/image/add", element: <ImageUploadComponent /> },
        { path: "/image/select/:positionId", element: <PageImageChangeComponent /> },

        { path: "/file/table", element: <FileTableComponent /> },
        { path: "/file/add", element: <FileUploadComponent /> },

        { path: "/page/table", element: <PageTableComponent /> },

        { path: "/section/table/:pageId", element: <SectionTableComponent /> },
        { path: "/section/edit/:sectionId", element: <SectionEditComponent /> },

        { path: "/gallery/table", element: <GalleryTableComponent /> },
        { path: "/gallery/create", element: <GalleryCreateEdit isEditMode={false} /> },
        { path: "/gallery/edit/:galleryId", element: <GalleryCreateEdit isEditMode={true} /> },

        { path: "/user/create", element: <UserCreateEditComponent isEditMode={false} /> },
        { path: "/user/edit/:userId", element: <UserCreateEditComponent isEditMode={true} /> },
        { path: "/user/table", element: <UserTableComponent isEditMode={false} /> },

        { path: "/home", element: <HomeComponent /> },
        { path: "/SetPassword", element: <SetPasswordComponent /> },
    ];

    return (
        <UserContext.Provider value={userState}>
            <SiteContext.Provider value={siteState}>
                <ToastContext.Provider value={toastState}>
                    <ChangesContext.Provider value={{ hasChanges, setHasChanges }}>
                        <AdminUsersContext.Provider value={{ ...adminUserNames, setAdminUserNames, refreshAdminUsers: fetchAdminUsers }}>
                            <NavigationComponent />
                            <ToastComponent toast={toast} onClose={() => setToast(null)} />

                            <Routes>
                                {/* Public pages */}
                                <Route element={<PublicLayout />}>
                                    <Route path="/" element={<LoginComponent />} />
                                    <Route path="/forgottenpassword" element={<ForgottenPasswordComponent />} />
                                    <Route path="/401" element={<UnauthorizedComponent />} />
                                    <Route path="/404" element={<NotFoundComponent />} />
                                </Route>

                                {/* Authenticated pages under MainLayout */}
                                <Route element={<MainLayout />}>
                                    {mainRoutes.map((r) => (
                                        <Route key={r.path} path={r.path} element={auth(r.element)} />
                                    ))}
                                    <Route path="/*" element={<NotFoundComponent />} />
                                </Route>
                            </Routes>
                        </AdminUsersContext.Provider>
                    </ChangesContext.Provider>
                </ToastContext.Provider>
            </SiteContext.Provider>
        </UserContext.Provider>
    );
}

export default App;